
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import Modal from '@/common/components/UI/Modal/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import NewTable from '@/common/new-components/NewTable/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import FileReader from '@/common/components/UI/FileReader/index.vue'

// module components
import InvoiceContent from '@/modules/InvoiceModule/components/InvoiceContent/index.vue'
import LtlTabs from '@/modules/InvoiceModule/components/LtlTabs/index.vue'
import InvoiceDeliveryHistory from '@/modules/InvoiceModule/components/InvoiceDeliveryHistory/index.vue'
import ChangeInvoiceNumber from '@/modules/InvoiceModule/components/ChangeInvoiceNumber/index.vue'
import InvoiceDocument from '@/modules/DocumentsModule/components/InvoiceDocument/index.vue'
import InvoiceActionsButton from '@/modules/InvoiceModule/components/InvoiceActionsButton/index.vue'
import SaveBoxModal from '@/modules/InvoiceModule/components/SaveBoxModal/index.vue'
import RestoreModal from '@/modules/InvoiceModule/components/RestoreModal/index.vue'
import InvoiceStatusRow from '@/modules/InvoiceModule/components/InvoiceStatusRow/index.vue'
import WaitListStatusModal from '@/modules/NewDispatcher/components/WaitListStatusModal/index.vue'

// composable
import { createToast } from 'mosha-vue-toastify'
import { useRoute, useRouter } from 'vue-router'

// types
import {
    InvoiceT,
    InvoiceTtn,
    RestoreInvoicePayload,
    SetDeclaredPricePayloadT,
    InvoiceCourierT,
} from '@/core/types/orders.types'

import { CashPaymentStatusByInvoice } from '@/new-services/types/cash'

// realisations
import { orders } from '@/core/realisations/orders.realisation'
import { pvz } from '@/core/realisations/pvz.realisation'
import { PVZGetByIDT } from '@/core/types/pvz.types'
import { companies } from '@/core/realisations/companies.realisation'

// validation
import useVuelidate from '@vuelidate/core'
import { decimal, required } from '@vuelidate/validators'

// composable
import useNotifications from '@/common/composable/useNotifications'
import useAccount from '@/common/composable/useAccount'

// @helpers
import cut from '@/common/helpers/cut'
import { oktell } from '@/core/realisations/oktell.realisation'
import { OktellWaitListItem } from '@/core/types/oktell.types'

import { LtlStatusColors } from '@/core/constants/columns/LTL.columns'
import { StatusHistoryItem } from '@/core/types/create_order.types'
import { DataState } from '@/core/classes/DataState.class'
import { dispatcher } from '@/core/realisations/dispatcher.realisation'
import { useFormData } from '@/common/composable/useFormData'
import { billing } from '@/core/realisations/billing.realisation'

import cashAPI from '@/new-services/cash.service'

import formatPriceToTenge from '@/modern/helpers/formatPriceToTenge'

const COLUMNS = [
    {
        key: 'createdAt',
        name: 'Дата создания',
    },
    {
        key: 'status',
        name: 'Статус',
    },
    {
        key: 'state',
        name: 'Состояние',
    },
    {
        key: 'user',
        name: 'Кто проставил',
    },
    {
        key: 'clientType',
        name: 'Когда проставил',
    },
    {
        key: 'comment',
        name: 'Комментарий',
    },
    {
        key: 'source',
        name: 'Источник создания',
    },
]

export const VERIFY_OPTIONS = [
    {
        id: 0,
        name: 'Отключен',
        value: '0',
    },
    {
        id: 1,
        name: 'Spark',
        value: '1',
    },
    {
        id: 2,
        name: 'Kaspi',
        value: '2',
    },
]

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        TheInput,
        TheSelect,
        FailedFetch,
        Loader,
        LtlTabs,
        'invoice-content': InvoiceContent,
        'invoice-delivery-history': InvoiceDeliveryHistory,
        'change-invoice-number': ChangeInvoiceNumber,
        'save-box-modal': SaveBoxModal,
        RestoreModal,
        Modal,
        InvoiceDocument,
        InvoiceActionsButton,
        NewTable,
        InvoiceStatusRow,
        Cheap,
        WaitListStatusModal,
        FileReader,
    },
    setup() {
        const { userIs } = useAccount()
        const { getFormData } = useFormData()

        const state = reactive({
            is_problems_loading: false,
            is_trips_loading: false,
            is_lo: false,
            is_loading: false,
            is_shortcoming: false,
            is_failed: false,
            is_pvz_failed: false,
            is_pvz_loading: false,
            is_cancelling: false,
            invoice: null as InvoiceT | null,
            invoice_print: null as InvoiceT | null,
            pvz: null as PVZGetByIDT | null,
            statuses_lo: [] as OktellWaitListItem[],
            currentTab: 'general',
            problems: [] as string[],
            status_history: [] as StatusHistoryItem[],
            //
            is_on_set_declared_price: false,
            is_print_loading: false,
            cargoName: null as string | null,
            declaredPrice: null as number | null,
            is_manager: false,
            transit: [] as { ttns: InvoiceTtn[] }[],
            //
            deliveryCourier: null as InvoiceCourierT | null,
            takeCourier: null as InvoiceCourierT | null,
        })

        const settings_state = reactive({
            is_loading: false,
            is_failed: false,
            partial_return: false,
        })

        const transcript_state = reactive({
            is_loading: false,
            is_created: false,
            is_failed: false,
            transcript_id: null as number | null
        })

        const cash_payment_state = reactive({
            is_loading: false,
            is_failed: false,
            item: null as CashPaymentStatusByInvoice | null
        })

        const properties_state = reactive({
            is_loading: false,
        })

        const data_state = reactive({
            statuses: new DataState<{ id: number }>({}),
        })

        const box_state = reactive({
            is_opened: false,
            is_loading: false,
        })

        const restore_state = reactive({
            is_opened: false,
            is_loading: false,
            //
            takeDate: null as string | null,
            periodId: null as number | null,
        })

        const predict_state = reactive({
            is_opened: false,
            is_loading: false,
            date: null as string | null,
        })

        const approve_state = reactive({
            is_opened: false,
            is_loading: false,
            is_notification_opened: false,
            //
            deliveredAt: null as string | null,
            attachments: [] as File[],
            receiverName: null as string | null,
        })

        const state_form$ = useVuelidate(
            {
                cargoName: { required },
                declaredPrice: { required, decimal },
            },
            state
        )

        const predict_state_form$ = useVuelidate(
            {
                date: { required },
            },
            predict_state
        )

        const approve_state_form$ = useVuelidate(
            {
                deliveredAt: { required },
                attachments: { required },
                receiverName: { required },
            },
            approve_state
        )

        const verify_state = reactive({
            is_opened: false,
            is_loading: false,
            verify: null as string | null,
        })

        const previous_state = reactive({
            is_opened: false,
            is_loading: false,
            code: null as string | null,
        })

        const previous_state_form$ = useVuelidate(
            {
                code: { required },
            },
            previous_state
        )

        const verify_state_form$ = useVuelidate(
            {
                verify: { required },
            },
            verify_state
        )

        const delivery_history = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as any[],
            client_sla_date: null as string | null,
        })

        const change_invoice_number_state = reactive({
            is_opened: false,
            is_loading: false,
        })

        const route = useRoute()
        const invoice_id = Number(route.params.invoice_id)
        const notifications = useNotifications()

        const updateInvoiceProperties = async () => {
            if (!invoice_id) return

            try {
                properties_state.is_loading = true

                await orders.updateProperties(invoice_id)
                notifications.notify({ type: 'success', title: 'Принудительное обновление успешно' })

                loadInvoice()
            } catch (error) {
                notifications.notifyError({ error, title: 'При принудительном обновлении произошла ошибка' })
            } finally {
                properties_state.is_loading = false
            }
        }

        const loadInvoiceProblems = async () => {
            if (!invoice_id) return

            try {
                state.is_problems_loading = true

                const response = await orders.getInvoiceProblems(invoice_id)
                state.problems = response.data.problems
            } catch (error) {
                console.error(error)
            } finally {
                state.is_problems_loading = false
            }
        }

        const loadStatusHistory = async () => {
            if (!state.invoice || !state.invoice.invoice_number) return

            try {
                state.is_loading = true
                const response = await orders.getInvoiceStatusHistory(state.invoice.invoice_number)
                state.status_history = response.data
            } catch (error) {
                console.error(error)
            } finally {
                state.is_loading = false
            }
        }

        const loadInvoiceStatuses = async (invoice_number: string | null) => {
            if (!invoice_number) return

            try {
                delivery_history.is_loading = true

                const response = await orders.getInvoiceStatuses(invoice_number)
                delivery_history.list = response.data

                if (response.data.length) {
                    const deliveredStatuses = response.data.filter(
                        (status) => status.status_code === 211
                    )

                    if (deliveredStatuses.length > 0) {
                        delivery_history.client_sla_date =
                            deliveredStatuses[deliveredStatuses.length - 1].client_sla_date
                    } else {
                        delivery_history.client_sla_date = null
                    }
                }

                delivery_history.is_failed = false
            } catch (error) {
                console.error(error)
                delivery_history.is_failed = false

                createToast(
                    {
                        title: 'При получении истории доставки накладной произошла ошибка',
                        description: 'Попробуйте ещё раз',
                    },
                    { type: 'danger' }
                )
            } finally {
                delivery_history.is_loading = false
            }
        }

        const loadPVZByID = async () => {
            try {
                if (
                    !state.invoice ||
                    !state.invoice.receiver ||
                    !state.invoice.receiver.distribution_center_id
                )
                    return
                state.is_pvz_loading = true

                const response = await pvz.getPVZByID(state.invoice.receiver.distribution_center_id)
                state.pvz = response.data

                state.is_pvz_failed = false
            } catch (error) {
                console.error(error)
                state.is_pvz_failed = true

                createToast(
                    {
                        title: 'При получении ПВЗ произошла ошибка',
                        description: 'Попробуйте ещё раз',
                    },
                    { type: 'danger' }
                )
            } finally {
                state.is_pvz_loading = false
            }
        }

        const loadTranscript = async (invoice_number: string) => {
            try {
                transcript_state.is_loading = true

                const transcriptResponse = await billing.getTranscriptForPrint(invoice_number)
                transcript_state.is_created = Boolean(transcriptResponse.data)
                transcript_state.transcript_id = transcriptResponse.data.id

                transcript_state.is_failed = false
            } catch (error) {
                console.error(error)
                transcript_state.is_failed = true
            } finally {
                transcript_state.is_loading = false
            }
        }

        const loadCashPayment = async () => {
            try {
                cash_payment_state.is_loading = true

                const response = await cashAPI.getCashPaymentStatusByInvoice(invoice_id)

                if (response.data.data) {
                    cash_payment_state.item = response.data.data
                }

                cash_payment_state.is_failed = false
            } catch (error) {
                console.error(error)
                cash_payment_state.is_failed = true
            } finally {
                cash_payment_state.is_loading = false
            }
        }

        const loadInvoiceSettings = async () => {
            if (!state.invoice) return

            try {
                settings_state.is_loading = true

                const response = await companies.getCompanySettings(state.invoice.company_id, { page: 1, limit: 9999 })
                const partialReturn = response.data.find((s) => s.key === 'partial_return')

                if (partialReturn) {
                    settings_state.partial_return = partialReturn.isActive
                }

                settings_state.is_failed = false
            } catch (error) {
                console.error(error)
                settings_state.is_failed = true
            } finally {
                settings_state.is_loading = false
            }
        }

        const loadInvoice = async () => {
            try {
                state.is_loading = true

                const response = await orders.getInvoice(invoice_id)
                state.invoice = response.data

                if (response.data.billing_enabled && response.data.payment_method_id === 3) {
                    loadTranscript(response.data.invoice_number)
                } else if (response.data.payment_method_id === 2 || response.data.payment_method_id === 4) {
                    loadCashPayment()
                }

                const couriersResponse = await orders.getInvoiceCouriers(invoice_id)
                state.deliveryCourier = couriersResponse.data.deliveryCourier
                state.takeCourier = couriersResponse.data.takeCourier

                if (response.data.manager) {
                    state.is_manager = userIs(response.data.manager.id)
                }

                await loadInvoiceStatuses(state.invoice.invoice_number)
                await loadPVZByID()
                await loadInvoiceSettings()

                state.is_failed = false
            } catch (error) {
                console.error(error)
                state.is_failed = true

                createToast(
                    {
                        title: 'При получении накладной произошла ошибка',
                        description: 'Попробуйте ещё раз',
                    },
                    { type: 'danger' }
                )
            } finally {
                state.is_loading = false
            }
        }

        const loadTripsInfo = async () => {
            try {
                state.is_trips_loading = true

                const response = await orders.getInvoiceTripsInfo(invoice_id)
                state.transit = response.data
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: `Ошибка при получении информации о транзите`,
                })
            } finally {
                state.is_trips_loading = false
            }
        }

        const setDeclaredPrice = () => {
            state.cargoName = state.invoice?.cargo_name || null
            state.declaredPrice = state.invoice?.declared_price
                ? Number(state.invoice?.declared_price)
                : null
        }

        const router = useRouter()

        const printInvoice = async () => {
            try {
                state.is_print_loading = true

                const response = await orders.getLTLInvoiceForPrint(invoice_id)
                state.invoice_print = response.data

                if (!state.invoice_print) return

                let title_tmp = document.title
                document.title = state.invoice_print.invoice_number

                setTimeout(() => {
                    window.print()
                    document.title = title_tmp
                }, 500)
            } catch (error) {
                console.error(error)

                createToast(
                    {
                        title: 'При печати накладной произошла ошибка',
                        description: 'Попробуйте ещё раз',
                    },
                    { type: 'danger' }
                )
            } finally {
                state.is_print_loading = false
            }
        }

        const restore = async (payload?: RestoreInvoicePayload) => {
            try {
                restore_state.is_loading = true

                await orders.restoreInvoice(invoice_id, payload)
                notifications.notify({
                    title: 'Накладная успешно восстановлена',
                    type: 'success',
                })

                closeRestore()
                await loadInvoice()
                setDeclaredPrice()
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: `Ошибка при восстановлении накладной`,
                })
            } finally {
                restore_state.is_loading = false
            }
        }

        const onSaveBox = () => (box_state.is_opened = true)
        const closeSaveBox = () => (box_state.is_opened = false)

        const openRestore = () => (restore_state.is_opened = true)
        const closeRestore = () => (restore_state.is_opened = false)

        const togglePredictDate = () => (predict_state.is_opened = !predict_state.is_opened)

        const onCancelApproveNotification = () => {
            approve_state.is_notification_opened = false
        }

        const onConfirmApproveNotification = () => {
            approve_state.is_notification_opened = false
            approve_state.is_opened = true
        }

        const toggleApproveDelivery = () => {
            if (approve_state.is_opened) {
                approve_state.is_opened = false
                return
            }

            if (state.status_history.length) {
                if (state.status_history.map((s) => s.status.code).includes(231)) {
                    approve_state.is_opened = !approve_state.is_opened
                } else if (Number(approveNotificationPrice.value) === 0) {
                    approve_state.is_opened = !approve_state.is_opened
                } else {
                    approve_state.is_notification_opened = true
                }
            }
        }

        const approveDelivery = async () => {
            approve_state_form$.value.$touch()
            if (approve_state_form$.value.$invalid || !state.invoice || !approve_state.deliveredAt)
                return

            try {
                approve_state.is_loading = true

                const deliveredAt = approve_state.deliveredAt.split('T').join(' ') + ':00'

                const payload = {
                    deliveredAt,
                    attachments: approve_state.attachments,
                    receiverName: approve_state.receiverName,
                }

                await orders.approveDelivery(state.invoice.id, getFormData(payload))
                notifications.notify({ title: 'Доставка закрыта', type: 'success' })

                toggleApproveDelivery()
                loadInvoice()
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При закрытии доставки прозиошла ошибка',
                })
            } finally {
                approve_state.is_loading = false
            }
        }

        const manageRestore = () => {
            if (!state.invoice || !state.invoice.status) return

            // if (state.invoice.status.code === 204) openRestore()
            // else if (state.invoice.status.code === 503) restore()
            openRestore()
        }

        const saveBox = async (packCode: string) => {
            if (!state.invoice) return

            try {
                box_state.is_loading = true

                await orders.saveBox(state.invoice.id, packCode)

                notifications.notify({
                    title: 'Коробка сохранена',
                    type: 'success',
                })

                closeSaveBox()
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'Ошибка при сохранении коробки',
                })
            } finally {
                box_state.is_loading = false
            }
        }

        const goToOrder = () => {
            if (!state.invoice) return
            router.replace(`/order/${state.invoice.order_id}`)
        }

        const handleDeclaredPrice = async () => {
            state_form$.value.$touch()
            if (
                state_form$.value.$invalid ||
                !state.declaredPrice ||
                !state.cargoName ||
                !state.invoice ||
                !state.invoice.id
            )
                return

            try {
                state.is_loading = true

                const payload: SetDeclaredPricePayloadT = {
                    declaredPrice: state.declaredPrice,
                    cargoName: state.cargoName,
                }

                await orders.setDeclaredPrice(state.invoice.id, payload)
                notifications.notify({
                    title: 'Обновлены данные об объявленной стоимости!',
                    type: 'success',
                })

                await loadInvoice()
                toggleDeclaredPrice()
                setDeclaredPrice()
            } catch (error) {
                console.error(error)

                notifications.notifyError({
                    error,
                    title: `Ошибка при обновлении данных об объявленной стоимости.`,
                })
            } finally {
                state.is_loading = false
            }
        }

        const toggleDeclaredPrice = () => {
            state.is_on_set_declared_price = !state.is_on_set_declared_price
        }

        const openChangeInvoiceNumber = () => {
            change_invoice_number_state.is_opened = true
        }

        const closeChangeInvoiceNumber = () => {
            change_invoice_number_state.is_opened = false
        }

        const changeInvoiceNumber = async (new_invoice_number: string) => {
            if (!state.invoice) return

            try {
                change_invoice_number_state.is_loading = true

                await orders.updateInvoiceNumber(invoice_id, new_invoice_number)

                state.invoice.invoice_number = new_invoice_number

                notifications.notify({ title: 'Номер накладной изменен', type: 'success' })
                closeChangeInvoiceNumber()
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При смене номера накладной произошла ошибка',
                })
            } finally {
                change_invoice_number_state.is_loading = false
            }
        }

        const loadWaitList = async () => {
            if (!state.invoice) return

            try {
                state.is_loading = true

                const params = {
                    // clientIds: [state.invoice.id, state.invoice.order_id],
                    invoiceIds: [state.invoice.id],
                    orderIds: [state.invoice.order_id],
                    isCompleted: 1,
                }
                const response = await oktell.getWaitList(params)
                state.statuses_lo = response.data
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При загрузке произошла ошибка',
                })
                console.error(error)
            } finally {
                state.is_loading = false
            }
        }

        const setPredictDate = async () => {
            predict_state_form$.value.$touch()
            if (predict_state_form$.value.$invalid || !state.invoice || !predict_state.date) return

            try {
                predict_state.is_loading = true

                const dateWithoutSecondsArray = predict_state.date.split('T')
                const date = dateWithoutSecondsArray.join(' ') + ':00'

                const params = {
                    code: 211,
                    date,
                    requestSource: 'BPMS',
                }
                await orders.setNewPredictDate(state.invoice.id, params)

                notifications.notify({
                    title: 'Дата доставки успешно перенесена',
                    type: 'success',
                })
                togglePredictDate()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'Произошла ошибка',
                })
            } finally {
                predict_state.is_loading = false
            }
        }

        const toggleLo = () => (state.is_lo = !state.is_lo)

        const getCheapClass = computed(() => {
            if (!state.invoice || !state.invoice.status) return ''

            const { id } = state.invoice.status

            if (LtlStatusColors[id]) return LtlStatusColors[id]
            else if (id === 51) return 'red'
            else if (![1, 4].includes(id)) return 'yellow'

            return ''
        })

        const setShortcoming = (isShortcoming: boolean) => {
            state.is_shortcoming = isShortcoming
        }

        const wait_list_state = reactive({
            is_opened: false,
            is_loading: false,
            order: null as { invoiceId: number } | null,
        })

        const startWaitList = () => {
            wait_list_state.order = { invoiceId: invoice_id }
            wait_list_state.is_opened = true
        }

        const startVerify = () => {
            verify_state.is_opened = true
        }

        const loadStatuses = async () => {
            try {
                data_state.statuses.is_loading = true

                const response = await orders.getPreviousStatuses()
                data_state.statuses.list = response.data
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'Произошла ошибка',
                })
            } finally {
                data_state.statuses.is_loading = false
            }
        }

        const startPreviousStatus = () => {
            loadStatuses()
            previous_state.is_opened = true
        }

        const cancelWaitList = () => {
            wait_list_state.is_opened = false
            wait_list_state.order = null
        }

        const cancelVerify = () => {
            verify_state.is_opened = false
            verify_state.verify = null
        }

        const cancelPreviousState = () => {
            previous_state.is_opened = false
            previous_state.code = null
        }

        const submitVerify = async () => {
            verify_state_form$.value.$touch()
            if (verify_state_form$.value.$invalid || !state.invoice) return

            try {
                verify_state.is_loading = true

                await orders.verify(state.invoice.id, Number(verify_state.verify))

                notifications.notify({
                    title: 'Данные успешно изменены!',
                    type: 'success',
                })
                cancelVerify()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'Произошла ошибка',
                })
            } finally {
                verify_state.is_loading = false
            }
        }

        const setWaitListStatus = async () => {
            cancelWaitList()
            loadInvoice()
        }

        const getProblems = computed(() => {
            if (!state.problems.length) {
                return '-'
            }

            return state.problems.join(', ')
        })

        const setDate = () => {
            if (!predict_state.date) {
                const today = new Date()
                const dd = String(today.getDate()).padStart(2, '0')
                const mm = String(today.getMonth() + 1).padStart(2, '0')
                const yyyy = today.getFullYear()

                predict_state.date = `${yyyy}-${mm}-${dd}T23:00`
            }
        }

        const loadInfo = async () => {
            setDate()
            loadTripsInfo()
            loadInvoiceProblems()
            await loadInvoice()
            loadStatusHistory()
            setDeclaredPrice()
            loadWaitList()
        }

        const setPreviousState = async () => {
            previous_state_form$.value.$touch()
            if (previous_state_form$.value.$invalid || !state.invoice) return

            try {
                previous_state.is_loading = true

                await orders.setPreviousStatus(state.invoice.id, previous_state.code as string)

                notifications.notify({
                    title: 'Данные успешно изменены!',
                    type: 'success',
                })
                cancelPreviousState()
                loadInfo()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'Произошла ошибка',
                })
            } finally {
                previous_state.is_loading = false
            }
        }

        onBeforeMount(loadInfo)

        const isShowSignedStatus = computed(() => {
            if (state.invoice) {
                if (state.invoice.isSigned !== null && typeof state.invoice.isSigned === 'number') {
                    return true
                }
            }

            return false
        })

        const approveNotificationPrice = computed(() => {
            if (state.invoice) {
                const cashSum = Number(state.invoice.cash_sum)
                const codPayment = Number(state.invoice.cod_payment)

                return formatPriceToTenge(cashSum + codPayment)
            }

            return formatPriceToTenge(0)
        })

        return {
            previous_state,
            data_state,
            approve_state,
            state,
            settings_state,
            wait_list_state,
            predict_state,
            box_state,
            restore_state,
            change_invoice_number_state,
            delivery_history,
            verify_state,
            transcript_state,
            cash_payment_state,
            properties_state,
            //
            state_form$,
            predict_state_form$,
            previous_state_form$,
            approve_state_form$,
            //
            updateInvoiceProperties,
            loadInvoice,
            printInvoice,
            toggleDeclaredPrice,
            loadInvoiceStatuses,
            loadPVZByID,
            setShortcoming,
            goToOrder,
            handleDeclaredPrice,
            openChangeInvoiceNumber,
            closeChangeInvoiceNumber,
            changeInvoiceNumber,
            restore,
            onSaveBox,
            togglePredictDate,
            toggleApproveDelivery,
            approveDelivery,
            closeSaveBox,
            openRestore,
            manageRestore,
            closeRestore,
            saveBox,
            cut,
            toggleLo,
            getCheapClass,
            getProblems,
            setPredictDate,
            submitVerify,
            loadStatuses,
            startWaitList,
            startVerify,
            startPreviousStatus,
            cancelVerify,
            cancelWaitList,
            setWaitListStatus,
            loadStatusHistory,
            cancelPreviousState,
            setPreviousState,
            loadInfo,
            onCancelApproveNotification,
            onConfirmApproveNotification,
            //
            COLUMNS,
            VERIFY_OPTIONS,
            isShowSignedStatus,
            approveNotificationPrice,
        }
    },
})
